import { Link } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import { useSite } from "@/lib/SiteContext";

export default function Footer() {
  const { footer, settings } = useSite();
  if (!footer) return null;

  const logo = (settings && settings.logo_light) || "/logo-white.png";

  return (
    <footer data-testid="site-footer" className="relative bg-mist-navy text-white overflow-hidden">
      <div className="container-mist relative pt-20 pb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-10 gap-y-8 lg:gap-y-10 items-start">
        <div className="sm:col-span-2 lg:col-span-2">
          <img src={logo} alt="MIST" className="w-[145px] md:w-[165px] lg:w-[190px] h-auto object-contain block max-w-none mb-5" />
          {footer.company_description && (
            <p className="text-sm text-white/70 leading-relaxed max-w-md">{footer.company_description}</p>
          )}
          <div className="mt-5 space-y-2.5 text-sm text-white/75">
            {footer.address && (
              <div className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 text-mist-gold shrink-0 mt-0.5" aria-hidden />
                <span data-testid="footer-address">{footer.address}</span>
              </div>
            )}
            {footer.email && (
              <div className="flex items-start gap-2.5">
                <Mail className="h-4 w-4 text-mist-gold shrink-0 mt-0.5" aria-hidden />
                <a
                  data-testid="footer-email"
                  href={`mailto:${footer.email}`}
                  className="hover:text-mist-gold transition-colors"
                >
                  {footer.email}
                </a>
              </div>
            )}
            {footer.phone && (
              <div className="flex items-start gap-2.5">
                <Phone className="h-4 w-4 text-mist-gold shrink-0 mt-0.5" aria-hidden />
                <a href={`tel:${footer.phone}`} className="hover:text-mist-gold transition-colors">
                  {footer.phone}
                </a>
              </div>
            )}
          </div>
        </div>

        {(footer.columns || []).map((col) => (
          <div key={col.heading} data-testid={`footer-col-${col.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="text-white/40 text-[11px] uppercase tracking-[0.24em] font-medium mb-4">
              {col.heading}
            </div>
            <ul className="space-y-2.5 text-[15px] text-white/80">
              {(col.links || []).map((l) => (
                <li key={l.href + l.label}>
                  <Link to={l.href} className="hover:text-white transition-colors duration-200 ease-out">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-white/10">
        <div className="container-mist py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-xs text-white/60">{footer.copyright_text || `© ${new Date().getFullYear()} MIST Quality Inspection and Logistics. All rights reserved.`}</div>
          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {(footer.legal_links || []).map((l) => (
              <li key={l.href}>
                <Link
                  to={l.href}
                  data-testid={`footer-legal-${l.href.replace(/[^a-z0-9]+/g, "-")}`}
                  className="text-xs text-white/60 hover:text-mist-gold transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
